import { cva, type VariantProps } from "class-variance-authority";
import type { ReactNode } from "react";
import { Icon, type IconName } from "./icon";
import { cn } from "@/lib/cn";

const badge = cva(
  "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium leading-5 whitespace-nowrap",
  {
    variants: {
      tone: {
        neutral: "bg-surface-sunken text-ink-soft",
        accent: "bg-accent-wash text-accent",
        good: "bg-good-wash text-good",
        warn: "bg-warn-wash text-warn",
        bad: "bg-bad-wash text-bad",
        outline: "border border-line text-muted",
      },
    },
    defaultVariants: { tone: "neutral" },
  },
);

interface BadgeProps extends VariantProps<typeof badge> {
  children: ReactNode;
  /** Status is never carried by colour alone: pair the tone with an icon. */
  icon?: IconName;
  className?: string;
}

export function Badge({ tone, icon, children, className }: BadgeProps) {
  return (
    <span className={cn(badge({ tone }), className)}>
      {icon && <Icon name={icon} size={16} className="-ml-0.5" />}
      {children}
    </span>
  );
}

/** Leave/attendance statuses map to one tone + icon pair, app-wide. */
export const STATUS_TONE = {
  approved: { tone: "good", icon: "check-circle" },
  pending: { tone: "warn", icon: "warning-circle" },
  rejected: { tone: "bad", icon: "x-circle" },
  // Withdrawn is neutral, not bad: leave handed back is not leave refused, and
  // wearing the same red as a rejection would read as one at a glance.
  withdrawn: { tone: "neutral", icon: "arrow-up" },
} as const satisfies Record<
  string,
  { tone: "good" | "warn" | "bad" | "neutral"; icon: IconName }
>;

/**
 * "Filed late": a leave or WFH request raised after the first day it covers.
 *
 * Shown only while the request is still pending, because that is when it
 * matters — the approver deciding it. Once decided, the flag cannot tell a late
 * request apart from leave HR entered after the fact on somebody's behalf (both
 * are rows created after their start date), so it would mislabel the latter.
 */
export function FiledLateBadge({ request }: { request: { status: string; filed_late?: boolean } }) {
  if (!request.filed_late || request.status !== "pending") return null;
  return (
    <Badge tone="warn" icon="info">
      Filed late
    </Badge>
  );
}

export function StatusBadge({ status }: { status: string }) {
  const key = status.toLowerCase() as keyof typeof STATUS_TONE;
  const config = STATUS_TONE[key];
  if (!config) return <Badge tone="neutral">{status}</Badge>;
  return (
    <Badge tone={config.tone} icon={config.icon}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </Badge>
  );
}
